import { Command } from 'cmdk'
import { useId, useMemo, useState, type JSX, type ReactNode } from 'react'

import { ReadOnlyValue, useReadOnly } from '../components/read-only.tsx'
import { Popover, PopoverContent, PopoverTrigger } from './popover.tsx'
import { Button } from './button.tsx'
import { Checkbox } from './checkbox.tsx'
import { Icon } from './icon.tsx'
import { cn } from '../lib/cn.ts'
import {
  merchantLabel,
  merchantsById,
  type Merchant
} from '../lib/merchants.ts'

/** What both pickers take, beyond the value they hold. */
interface MerchantPickerBase {
  'data-testid': string
  /** Names the control. Read out with what is selected. */
  label: string
  /** The directory, or undefined while it loads. */
  merchants: readonly Merchant[] | undefined
  /** The directory is on the wire. The list says so rather than reading as empty. */
  pending?: boolean
  /** Why the directory could not be read. The picker still works; a reader types an id. */
  error?: string | null
  /** A message under the control. The error wins when there is one. */
  hint?: ReactNode
}

export type MerchantSelectProps = MerchantPickerBase & {
  /** The selected merchant id, as text. Empty means none. */
  value: string
  onChange: (value: string) => void
  /**
   * The text of the "no merchant" choice, for example `All merchants`. Absent
   * means the control requires one, and offers no way back to empty.
   */
  placeholder?: string
}

export type MerchantMultiSelectProps = MerchantPickerBase & {
  /** The selected merchant ids. Empty means every merchant. */
  value: readonly number[]
  onChange: (value: number[]) => void
  /** What the trigger says when nothing is selected. */
  placeholder?: string
}

/** Digits only, the way every org id on this platform is spelled. */
const ORG_ID_RE = /^[0-9]{1,18}$/

const ROW_CLASS = cn(
  'flex cursor-pointer items-center gap-2 px-2 py-1.5 text-sm',
  'data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground'
)

/**
 * What cmdk matches a row against: the name and the id together.
 *
 * Both, because a reader arrives with either. The id is also what keeps the
 * value unique — two merchants here genuinely share a name, and cmdk treats a
 * repeated value as the same row.
 *
 * @param merchant - The row.
 * @returns The searchable value.
 */
export function searchValue(merchant: Merchant): string {
  return `${merchantLabel(merchant)} ${String(merchant.orgId)}`
}

/**
 * Which merchants the list offers.
 *
 * A dormant merchant is hidden by default: half this directory is inactive, and
 * a list where every other row is a merchant nobody can transact with is a list
 * a reader scrolls past rather than reads. Whatever is already selected is
 * always offered, so a filter never loses the row it points at.
 *
 * @param merchants - The whole directory.
 * @param showInactive - Whether the reader asked for the dormant ones.
 * @param selected - Ids that must appear whatever their state.
 * @returns The rows to render.
 */
export function visibleMerchants(
  merchants: readonly Merchant[] | undefined,
  showInactive: boolean,
  selected: readonly number[]
): Merchant[] {
  return (merchants ?? []).filter(
    merchant =>
      showInactive || merchant.isActive || selected.includes(merchant.orgId)
  )
}

/**
 * The id a reader typed that the directory does not hold.
 *
 * Read off the search box rather than the selected value: a reader typing an
 * id has not selected anything yet, so there would otherwise be no row to
 * select. Returns null as soon as the list already offers that id.
 *
 * @param search - What is in the search box.
 * @param rows - The rows the list is showing.
 * @returns The id to offer as its own row, or null.
 */
export function unlistedOrgId(
  search: string,
  rows: readonly Merchant[]
): string | null {
  const typed = search.trim()
  if (!ORG_ID_RE.test(typed)) {
    return null
  }

  return rows.some(row => String(row.orgId) === typed) ? null : typed
}

/**
 * The shared shell: a label, the trigger, the popover and one message slot.
 *
 * The trigger is built here rather than passed in, because the message under
 * the control has to be wired to it with `aria-describedby` — a refusal a
 * sighted reader sees under the control and a screen reader never hears is the
 * failure this owns. `Input` and `Select` carry the same contract.
 */
function PickerShell({
  testId,
  label,
  triggerText,
  open,
  onOpenChange,
  error,
  hint,
  children
}: {
  testId: string
  label: string
  triggerText: string
  open: boolean
  onOpenChange: (open: boolean) => void
  error?: string | null
  hint?: ReactNode
  children: ReactNode
}): JSX.Element {
  const labelId = useId()
  const message = error ?? hint
  const messageId = `${labelId}-message`

  return (
    <div data-testid={testId} className="grid w-full min-w-0 gap-2 sm:w-64">
      <span id={labelId} className="type-eyebrow">
        {label}
      </span>
      <Popover open={open} onOpenChange={onOpenChange}>
        <PopoverTrigger asChild>
          <Button
            data-testid={`${testId}-trigger`}
            variant="outline"
            aria-label={`${label}: ${triggerText}`}
            aria-invalid={error ? true : undefined}
            aria-describedby={message ? messageId : undefined}
            className="w-full max-w-full min-w-0 justify-between"
          >
            <span className="truncate">{triggerText}</span>
            <Icon
              name="chevrons-up-down"
              className="size-4 shrink-0 text-muted-foreground"
            />
          </Button>
        </PopoverTrigger>
        {/*
          Radix renders this as `role="dialog"`, and the visible label sits
          outside it — so without a name of its own it announces as "dialog"
          and nothing else to a reader who has just opened it.

          It always drops below the control. Flipped above, on a phone whose
          keyboard takes the room below, the search box ran off the top of the
          screen where nobody could scroll to it. Below, the page scrolls.
        */}
        <PopoverContent
          data-testid={`${testId}-content`}
          aria-label={label}
          side="bottom"
          avoidCollisions={false}
          align="start"
          className="popover-in-viewport w-80 p-0"
        >
          {children}
        </PopoverContent>
      </Popover>
      {message ? (
        <p
          id={messageId}
          // Balanced, so a hint held to the picker's width never ends on one
          // word alone.
          className={cn(
            'text-sm text-balance',
            error ? 'text-destructive' : 'text-muted-foreground'
          )}
        >
          {message}
        </p>
      ) : null}
    </div>
  )
}

/**
 * The search box, the scrolling list and the "show inactive" toggle, in one
 * cmdk frame.
 *
 * cmdk's own filter is left on: it scores a row against `searchValue`, so
 * typing part of a name and typing the id both find the row, and the
 * highlighted row moves with the arrow keys without this file owning a
 * selection model.
 */
function PickerList({
  testId,
  pending,
  search,
  onSearchChange,
  showInactive,
  onShowInactiveChange,
  header,
  children
}: {
  testId: string
  /** Sits between the search box and the list, clear of the search. */
  header?: ReactNode
  pending?: boolean
  search: string
  onSearchChange: (next: string) => void
  showInactive: boolean
  onShowInactiveChange: (next: boolean) => void
  children: ReactNode
}): JSX.Element {
  return (
    <Command label="Search merchants">
      <div className="flex items-center gap-2 border-b px-3">
        <Icon name="search" className="text-muted-foreground size-4" />
        <Command.Input
          data-testid={`${testId}-search`}
          value={search}
          onValueChange={onSearchChange}
          placeholder="Search by name or id"
          className="placeholder:text-muted-foreground h-9 w-full bg-transparent text-sm outline-none"
        />
      </div>
      {header}
      <Command.List className="max-h-64 overflow-y-auto p-1">
        {pending ? (
          <p
            data-testid={`${testId}-pending`}
            role="status"
            className="text-muted-foreground p-3 text-sm"
          >
            {'Loading merchants…'}
          </p>
        ) : (
          <Command.Empty className="text-muted-foreground p-3 text-sm">
            {'No merchant matches.'}
          </Command.Empty>
        )}
        {children}
      </Command.List>
      <Checkbox
        data-testid={`${testId}-show-inactive`}
        checked={showInactive}
        label="Show inactive merchants"
        labelClassName="text-muted-foreground border-t px-3 py-2 text-xs"
        onChange={event => {
          onShowInactiveChange(event.target.checked)
        }}
      />
    </Command>
  )
}

/** One row: the name, the id in mono, and a mark when it is chosen. */
function MerchantRow({
  testId,
  merchant,
  selected,
  onSelect
}: {
  testId: string
  merchant: Merchant
  selected: boolean
  onSelect: () => void
}): JSX.Element {
  return (
    <Command.Item
      data-testid={`${testId}-option-${String(merchant.orgId)}`}
      value={searchValue(merchant)}
      aria-checked={selected}
      onSelect={onSelect}
      className={ROW_CLASS}
    >
      <Icon
        name="check"
        className={cn('size-4 shrink-0', selected ? undefined : 'opacity-0')}
      />
      <span className="truncate">{merchantLabel(merchant)}</span>
      {merchant.isActive ? null : (
        <span className="text-muted-foreground text-xs">{'Inactive'}</span>
      )}
      <span className="text-muted-foreground ml-auto font-mono text-xs">
        {merchant.orgId}
      </span>
    </Command.Item>
  )
}

/**
 * MerchantSelect — one merchant, picked by name.
 *
 * The control this console reaches for wherever a reader used to type an org
 * id. It is a searchable list rather than the native `Select` the rest of the
 * console uses; `docs/decisions/0009-a-merchant-is-picked-not-typed.md` records
 * why merchants are the exception to ADR-0006.
 *
 * **A raw id is still accepted.** The directory reads a datalake replica, so a
 * merchant onboarded minutes ago may not be listed. Typing an id that matches
 * nothing offers it as its own row, and choosing that row sends the id — a
 * reader is never blocked by a list that has not caught up. That is what makes
 * this a safe replacement for a text box on a screen where the merchant is
 * required.
 *
 * @param props - MerchantSelect props.
 * @param props.label - Names the control.
 * @param props.merchants - The directory, or undefined while it loads.
 * @param props.value - The selected merchant id, as text.
 * @param props.onChange - Called with the next id, or an empty string.
 * @param props.placeholder - The "no merchant" choice. Absent means one is required.
 * @param props.pending - The directory is on the wire.
 * @param props.error - Why the directory could not be read.
 * @param props.hint - Shown when there is no error.
 * @returns The rendered control.
 */
export function MerchantSelect({
  'data-testid': testId,
  label,
  merchants,
  value,
  onChange,
  placeholder,
  pending,
  error,
  hint
}: MerchantSelectProps): JSX.Element {
  const readOnly = useReadOnly()
  const [open, setOpen] = useState(false)
  const [showInactive, setShowInactive] = useState(false)
  const [search, setSearch] = useState('')
  const selectedId = ORG_ID_RE.test(value) ? Number(value) : null

  /**
   * Take a choice and shut the list.
   *
   * One merchant is the whole answer here, so leaving the list open would
   * make the reader dismiss it themselves every time. The search box is
   * cleared with it: reopening on last week's search would hide the row the
   * reader just chose.
   *
   * @param next - The id chosen, or an empty string for "no merchant".
   */
  function choose(next: string): void {
    onChange(next)
    setSearch('')
    setOpen(false)
  }
  const byId = useMemo(() => merchantsById(merchants), [merchants])
  const selected = selectedId === null ? undefined : byId.get(selectedId)
  const rows = visibleMerchants(
    merchants,
    showInactive,
    selectedId === null ? [] : [selectedId]
  )
  const unlisted = unlistedOrgId(search, rows)

  const triggerText = selected
    ? merchantLabel(selected)
    : selectedId !== null
      ? `Merchant ${String(selectedId)}`
      : (placeholder ?? 'Choose a merchant')

  if (readOnly) {
    return <ReadOnlyValue testId={testId} label={label} value={triggerText} />
  }

  return (
    <PickerShell
      testId={testId}
      label={label}
      error={error}
      hint={hint}
      triggerText={triggerText}
      open={open}
      onOpenChange={setOpen}
    >
      <PickerList
        testId={testId}
        pending={pending}
        search={search}
        onSearchChange={setSearch}
        showInactive={showInactive}
        onShowInactiveChange={setShowInactive}
      >
        {placeholder === undefined ? null : (
          <Command.Item
            data-testid={`${testId}-option-none`}
            value={placeholder}
            onSelect={() => {
              choose('')
            }}
            className={ROW_CLASS}
          >
            <Icon
              name="check"
              className={cn(
                'size-4 shrink-0',
                value === '' ? undefined : 'opacity-0'
              )}
            />
            <span>{placeholder}</span>
          </Command.Item>
        )}
        {rows.map(merchant => (
          <MerchantRow
            key={merchant.orgId}
            testId={testId}
            merchant={merchant}
            selected={merchant.orgId === selectedId}
            onSelect={() => {
              choose(String(merchant.orgId))
            }}
          />
        ))}
        {unlisted === null ? null : (
          <Command.Item
            data-testid={`${testId}-option-unlisted`}
            value={unlisted}
            onSelect={() => {
              choose(unlisted)
            }}
            className={ROW_CLASS}
          >
            <Icon name="check" className="size-4 shrink-0 opacity-0" />
            <span>{`Use merchant id ${unlisted}`}</span>
          </Command.Item>
        )}
      </PickerList>
    </PickerShell>
  )
}

/**
 * Whether two selections hold the same merchants, in any order.
 *
 * @param a - One selection.
 * @param b - The other.
 * @returns True when both hold exactly the same ids.
 */
function sameSelection(a: readonly number[], b: readonly number[]): boolean {
  const ids = new Set(a)
  return ids.size === new Set(b).size && b.every(id => ids.has(id))
}

/**
 * MerchantMultiSelect — several merchants, picked by name, with one box at the
 * top that ticks or unticks every merchant.
 *
 * Empty means every merchant, not none: the reports this serves are
 * platform-wide until a reader narrows them, and a control that meant "no
 * merchant" when untouched would answer an empty report on first paint. So the
 * list opens with every row ticked, and a reader who wants most merchants
 * unticks the few they do not.
 *
 * **Every listed merchant ticked is every merchant**, the dormant ones the list
 * hides included, so it commits as empty rather than as today's ids. **Nothing
 * ticked is every merchant too**: an empty report is never what the reader
 * meant.
 *
 * **A dormant merchant stays hidden until the reader asks for it**, ticked or
 * not. A hidden row keeps its tick, so it still commits with the rest.
 *
 * Choosing a row does not close the list. Picking five merchants is the case
 * this control exists for, and a popover that shut after each one would make
 * that five trips through the trigger.
 *
 * **The selection commits once, when the list shuts.** While it is open a
 * toggle changes a draft, and the rows and the trigger show that draft.
 * Shutting the list by any route — Escape, a click outside, the trigger —
 * calls `onChange` once with the draft, and not at all when the draft holds
 * the same merchants as `value`. A caller that keys a query on the selection
 * therefore runs one query for five merchants rather than five. A `value` that
 * changes while the list is open does not replace the draft; the close commits
 * the draft.
 *
 * @param props - MerchantMultiSelect props.
 * @param props.label - Names the control.
 * @param props.merchants - The directory, or undefined while it loads.
 * @param props.value - The committed merchant ids. Empty means every merchant.
 * @param props.onChange - Called with the whole next selection when the list shuts.
 * @param props.placeholder - What the trigger says when every merchant is selected.
 * @param props.pending - The directory is on the wire.
 * @param props.error - Why the directory could not be read.
 * @param props.hint - Shown when there is no error.
 * @returns The rendered control.
 */
export function MerchantMultiSelect({
  'data-testid': testId,
  label,
  merchants,
  value,
  onChange,
  placeholder = 'All merchants',
  pending,
  error,
  hint
}: MerchantMultiSelectProps): JSX.Element {
  const readOnly = useReadOnly()
  const [open, setOpen] = useState(false)
  const [showInactive, setShowInactive] = useState(false)
  const [search, setSearch] = useState('')
  // Null while shut. `every` is every merchant ticked, whatever the directory
  // holds by the time it loads. Only a close commits it, so unmounting with the
  // list open discards the draft and calls nothing.
  const [draft, setDraft] = useState<number[] | 'every' | null>(null)
  const byId = useMemo(() => merchantsById(merchants), [merchants])
  const picked = draft ?? (value.length === 0 ? 'every' : value)
  // No ticked row is kept on the list here. Unticking one merchant from every
  // merchant ticks every dormant one, and keeping those would show all of them.
  const rows = visibleMerchants(merchants, showInactive, [])
  const isTicked = (orgId: number): boolean =>
    picked === 'every' || picked.includes(orgId)
  const allTicked =
    picked === 'every' ||
    (rows.length > 0 && rows.every(row => picked.includes(row.orgId)))

  function openChange(next: boolean): void {
    if (next) {
      setDraft(value.length === 0 ? 'every' : [...value])
    } else {
      if (draft !== null) {
        const committed =
          allTicked || draft === 'every' || draft.length === 0 ? [] : draft
        if (!sameSelection(committed, value)) {
          onChange(committed)
        }
      }
      setDraft(null)
      setSearch('')
    }
    setOpen(next)
  }

  function toggle(orgId: number): void {
    // Unticking one from every merchant keeps the rest of the directory,
    // the dormant rows the list hides included.
    const base =
      picked === 'every'
        ? (merchants ?? []).map(merchant => merchant.orgId)
        : picked
    setDraft(
      base.includes(orgId) ? base.filter(id => id !== orgId) : [...base, orgId]
    )
  }

  const shown = allTicked ? [] : picked
  const triggerText =
    shown.length === 0
      ? placeholder
      : shown.length === 1
        ? merchantLabel(byId.get(shown[0]))
        : `${String(shown.length)} merchants`

  if (readOnly) {
    return <ReadOnlyValue testId={testId} label={label} value={triggerText} />
  }

  return (
    <PickerShell
      testId={testId}
      label={label}
      error={error}
      hint={hint}
      triggerText={triggerText}
      open={open}
      onOpenChange={openChange}
    >
      <PickerList
        testId={testId}
        pending={pending}
        search={search}
        onSearchChange={setSearch}
        showInactive={showInactive}
        onShowInactiveChange={setShowInactive}
        header={
          <Checkbox
            data-testid={`${testId}-all`}
            checked={allTicked}
            label={allTicked ? 'Unselect all' : 'Select all'}
            labelClassName="cursor-pointer border-b px-3 py-2 text-sm font-semibold"
            onChange={() => {
              setDraft(allTicked ? [] : 'every')
            }}
          />
        }
      >
        {rows.map(merchant => (
          <MerchantRow
            key={merchant.orgId}
            testId={testId}
            merchant={merchant}
            selected={isTicked(merchant.orgId)}
            onSelect={() => {
              toggle(merchant.orgId)
            }}
          />
        ))}
      </PickerList>
    </PickerShell>
  )
}
